import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";

/**
 * Tab 结构: { id: string, path: string, label: string }
 */
const TabContext = createContext(null);

export const useTabContext = () => {
  const ctx = useContext(TabContext);
  if (!ctx) {
    throw new Error("useTabContext must be used within TabProvider");
  }
  return ctx;
};

const initialState = (pathToLabel, initialPath) => {
  const path = initialPath === "/" ? "/dashboard" : initialPath;
  const label = pathToLabel(path) || "未命名";
  const firstTab = { id: generateTabId(), path, label };
  return { tabs: [firstTab], activeTabId: firstTab.id };
};

export const TabProvider = ({ children, pathToLabel, initialPath }) => {
  const [state, setState] = useState(() => initialState(pathToLabel, initialPath));
  const { tabs, activeTabId } = state;

  const setActiveTabId = useCallback((id) => {
    setState((prev) => ({ ...prev, activeTabId: id }));
  }, []);

  const addOrSwitchTab = useCallback(
    (path, label) => {
      const normalizedPath = path === "/" ? "/dashboard" : path;
      const displayLabel = label || pathToLabel(normalizedPath) || "未命名";

      setState((prev) => {
        const existing = prev.tabs.find((t) => t.path === normalizedPath);
        if (existing) {
          return { ...prev, activeTabId: existing.id };
        }
        const newTab = { id: generateTabId(), path: normalizedPath, label: displayLabel };
        return {
          tabs: [...prev.tabs, newTab],
          activeTabId: newTab.id,
        };
      });
    },
    [pathToLabel]
  );

  const closeTab = useCallback((tabId) => {
    setState((prev) => {
      const index = prev.tabs.findIndex((t) => t.id === tabId);
      if (index === -1) return prev;
      const closedTab = prev.tabs[index];
      const newTabs = prev.tabs.filter((t) => t.id !== tabId);
      if (closedTab.id === prev.activeTabId && newTabs.length > 0) {
        const newActiveIndex = Math.min(index, newTabs.length - 1);
        return { tabs: newTabs, activeTabId: newTabs[newActiveIndex].id };
      }
      return { ...prev, tabs: newTabs };
    });
  }, []);

  const switchTab = useCallback((tabId) => {
    setActiveTabId(tabId);
  }, [setActiveTabId]);

  const getActiveTab = useCallback(() => {
    return tabs.find((t) => t.id === activeTabId);
  }, [tabs, activeTabId]);

  const value = useMemo(
    () => ({
      tabs,
      activeTabId,
      addOrSwitchTab,
      closeTab,
      switchTab,
      setActiveTabId,
      getActiveTab,
      pathToLabel,
    }),
    [
      tabs,
      activeTabId,
      addOrSwitchTab,
      closeTab,
      switchTab,
      setActiveTabId,
      getActiveTab,
      pathToLabel,
    ]
  );

  return <TabContext.Provider value={value}>{children}</TabContext.Provider>;
};

const generateTabId = () => `tab_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

export default TabContext;
